import { useEffect, useRef, useState } from "react";
import SEO from "../components/SEO";

export default function Publications() {
  const publicationsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Publications",
    "url": "https://www.thrombio.com.au/publications",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Publications",
      "description": "Thrombio's technology is built around state-of-the-art science and technology, published by internationally recognized leaders in the field of drug discovery and experimental biomedicine."
    }
  };

  return (
    <div className="publicationsBg -mb-60 pb-60">
      <SEO
        title="Publications"
        description="Unlock insights into ThromBio's research: Professor Jackson and his team have been undertaking cutting edge research into novel Stroke therapies for 25 years"
        keywords={['Medical Publications', 'Thromboinflammation', 'Research Papers', 'Antiplatelet Therapy', 'Arterial Thrombosis']}
        schema={publicationsSchema}
      />
      
      <div className="pageContainer flex flex-col gap-[140px] md:gap-[120px] xl:gap-[150px] pt-40 md:pt-[155px] xl:pt-[180px]">
        <div className="flex flex-col gap-32">
          <div className="flex flex-col gap-[93px]">
            {/* Optimized H1 tag with visible text preserved */}
            <div className="text-white drop-shadow-[0_4px_8.9px_#000] text-[4.8rem] md:text-[5rem] font-bold leading-[5.2rem] tracking-[.192rem]">
              <h1 className="sr-only">ThromBio Publications: Leading Research in Stroke Therapy</h1>
              Publications
            </div>
            {/* Optimized H2 tag with visible text preserved */}
            <div className="font-syne text-[3.2rem] font-bold leading-[3.6rem] tracking-[.16rem] text-[#E0FFFF]">
              <h2 className="sr-only">Research Papers: Breakthroughs in Stroke Treatment</h2>
              Review Articles
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-12 gap-y-24">
            <PublicationCard
              link="https://doi.org/10.1182/blood-2018-11-882993"
              imgSrc="/thrombioInflammation.png"
              pubYear={2019}
              altText="Blood 2019"
            >
              <p>
                Thromboinflammation: challenges of therapeutically targeting coagulation and other host defense
                mechanisms.
              </p>
              <p>Jackson et a, 2019.</p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1038/nm.2515" 
              imgSrc="/arterialThrombosis.png" 
              pubYear={2011}
              altText="Nature Medicine 2011"
            >
              <p>Arterial thrombosis - insidious, unpredictable and deadly.</p>
              <p>Jackson, 2011.</p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1007/82_2010_61" 
              imgSrc="/ra3.png" 
              pubYear={2011}
              altText="Phosphoinositide 3-kinase in Health and Disease 2011"
            >
              <p>PI 3-Kinase p110β regulation of platelet integrin α(IIb)β3.</p>
              <p>Jackson and Schoenwaelder, 2010.</p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1038/nrd1198" 
              imgSrc="/ra4.png" 
              pubYear={2003}
              altText="Nature Reviews 2003"
            >
              <p>Antiplatelet therapy: in search of the 'magic bullet'.</p>
              <p>Jackson and Schoenwaelder, 2003.</p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1007/s00018-006-6001-2" 
              imgSrc="/ra5.png" 
              pubYear={2006}
              altText="Cellular and Molecular Life Sciences 2006"
            >
              <p>Type I phosphoinositide 3-kinases: potential antithrombotic targets?</p>
              <p>Jackson and Schoenwaelder, 2006.</p>
            </PublicationCard>
          </div>
        </div>
        <div className="flex flex-col gap-[93px]">
          {/* Optimized H2 tag with visible text preserved */}
          <div className="font-bold text-[#E0FFFF] font-syne text-[3.2rem] leading-[3.6rem] tracking-[.16rem]">
            <h3 className="sr-only">Clinical Studies: Advancing Stroke Care with TBO-309</h3>
            Primary Research Articles
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-10">
            <PublicationCard 
              link="https://doi.org/10.1038/s41563-019-0323-6" 
              imgSrc="/pr9.png" 
              pubYear={2019}
              altText="Nature Materials 2019"
            >
              <p>An integrin α IIb β 3 intermediate aﬃnity state mediates biomechanical platelet aggregation.</p>
              <p>
                Chen <i>et al</i>, 2019.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1038/s41467-018-03430-6" 
              imgSrc="/pr8.png" 
              pubYear={2018}
              altText="Nature Communications 2018"
            >
              <p>Compression force sensing regulates integrin αIIbβ3 adhesive function on diabetic platelets.</p>
              <p>
                Ju <i>et al</i>, 2018.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1182/blood-2017-06-789032" 
              imgSrc="/pr7.png" 
              pubYear={2017}
              altText="Blood 2017"
            >
              <p>Endogenous fibrinolysis facilitates clot retraction in vivo.</p>
              <p>
                Samson <i>et al</i>, 2017.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1074/jbc.M109.029132" 
              imgSrc="/pr6.png" 
              pubYear={2010}
              altText="Journal of Biological Chemistry 2010"
            >
              <p>
                Phosphoinositide 3-kinase p110 beta regulates integrin alpha IIb beta 3 avidity and the cellular
                transmission of contractile forces.
              </p>
              <p>
                Schoenwaelder <i>et al</i>, 2010.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1038/nm.1955" 
              imgSrc="/pr5.png" 
              pubYear={2009}
              altText="Nature Medicine 2009"
            >
              <p>A shear gradient-dependent platelet aggregation mechanism drives thrombus formation.</p>
              <p>
                Nesbitt <i>et al</i>, 2009.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="http://dx.doi.org/10.1074/jbc.M704358200" 
              imgSrc="/pr4.png" 
              pubYear={2007}
              altText="Journal of Biological Chemistry 2007"
            >
              <p>
                Identification of a unique co-operative phosphoinositide 3-kinase signaling mechanism regulating integrin
                aIIbb3 adhesive function in platelets.
              </p>
              <p>
                Schoenwaelder <i>et al</i>, 2007.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="http://doi.org/10.1074/jbc.M410235200" 
              imgSrc="/pr3.png" 
              pubYear={2005}
              altText="Journal of Biological Chemistry 2005"
            >
              <p>
                Importance of temporal flow gradients and integrin aIIbb3 mechanotransduction for shear activation of
                platelets.
              </p>
              <p>
                Goncalves <i>et al</i>, 2005.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1038/nm1232" 
              imgSrc="/pr2.png" 
              pubYear={2005}
              altText="Nature Medicine 2005"
            >
              <p>PI 3-kinase p110beta: a new target for antithrombotic therapy.</p>
              <p>
                Jackson <i>et al</i>, 2005.
              </p>
            </PublicationCard>
            <PublicationCard 
              link="https://doi.org/10.1182/blood.V99.1.151" 
              imgSrc="/pr1.png" 
              pubYear={2002}
              altText="Blood 2002"
            >
              <p>
                Essential role for phosphoinositide 3-kinase in shear-dependent signaling between platelet glycoprotein
                Ib/V/IX and integrin a(IIb)b3.
              </p>
              <p>
                Yap <i>et al</i>, 2002.
              </p>
            </PublicationCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function PublicationCard(props) {
  const { link, imgSrc, children, altText, pubYear } = props;

  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(elementRef.current);
        }
      },
      { threshold: 0.5 } // Trigger when 50% of the element is visible
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={elementRef} className={`slide-in ${isVisible ? "active" : ""} flex flex-col gap-10 items-center`}>
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        className="relative group !no-underline w-[165px] h-[218px] md:w-[185px] md:h-[250px]"
      >
        <img src={imgSrc} alt={altText} className="absolute left-0 top-0 shadow-xl" />
        <div className="relative h-full bg-black/70 z-1 text-white flex flex-col justify-between p-4 transition opacity-0 group-hover:opacity-100 -translate-x-1/2 scale-x-0 group-hover:scale-x-100 group-hover:translate-x-0 drop-shadow-sm duration-300">
          {children}
        </div>
        <img src={imgSrc} alt={altText} className="invisible" />
      </a>
      <div className="font-bold">{pubYear}</div>
    </div>
  );
}